import React, { useCallback, useEffect, useState } from 'react';
import { Text, Title } from '@mantine/core';
import { Outlet } from 'react-router-dom';

import SystemMaintenanceImage from '../assets/system_maintenance_illustration.png';
import logo from '../assets/cb-logo.svg';
import { Loader } from '../components/common';

const HEALTH_CHECK_INTERVAL_MS = 15000;
const RETRY_COUNT = 3;
const RETRY_DELAY_MS = 1000;
const FETCH_TIMEOUT_MS = 10000;
const INTERNET_CHECK_TIMEOUT_MS = 5000;

const API_BASE_URL =
    import.meta.env.VITE_API_URL ||
    import.meta.env.VITE_APOLLO_URI?.replace(/\/graphql$/, '');
const INTERNET_CHECK_URL = 'https://www.cloudflare.com/cdn-cgi/trace';

function delay(ms: number): Promise<void> {
    return new Promise(resolve => {
        setTimeout(resolve, ms);
    });
}

type ApiHealth =
    | { reachable: true; maintenanceMode: boolean; message: string | null }
    | { reachable: false };

type HealthStatus =
    | { reachable: true; maintenanceMode: boolean; message: string | null }
    | { reachable: false; networkProblem: boolean };

async function fetchWithTimeout(
    url: string,
    timeoutMs: number,
    options?: RequestInit,
): Promise<Response> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const res = await fetch(url, { ...options, signal: controller.signal });
        return res;
    } finally {
        clearTimeout(timeout);
    }
}

async function isNetworkProblem(): Promise<boolean> {
    try {
        await fetchWithTimeout(INTERNET_CHECK_URL, INTERNET_CHECK_TIMEOUT_MS, {
            mode: 'no-cors',
        });
        return false;
    } catch {
        return true;
    }
}

async function checkApiHealth(): Promise<ApiHealth> {
    try {
        const res = await fetchWithTimeout(API_BASE_URL, FETCH_TIMEOUT_MS);
        if (!res.ok) {
            return { reachable: false };
        }

        const data = await res.json();
        const isDegraded =
            data.status === 'DEGRADED' || data.db === 'disconnected';
        return {
            reachable: true,
            maintenanceMode: data.maintenanceMode || isDegraded,
            message: data.maintenanceMessage ?? null,
        };
    } catch {
        return { reachable: false };
    }
}

async function checkHealthWithRetries(attempt = 0): Promise<HealthStatus> {
    const apiHealth = await checkApiHealth();
    if (apiHealth.reachable) {
        return apiHealth;
    }

    if (attempt >= RETRY_COUNT) {
        const networkProblem = await isNetworkProblem();
        return { reachable: false, networkProblem };
    }

    await delay(RETRY_DELAY_MS);
    return checkHealthWithRetries(attempt + 1);
}

type PageStatus =
    | { status: 'loading' }
    | { status: 'ok' }
    | {
          status: 'maintenance';
          message: string | null;
          isNetworkProblem: boolean;
      };

function getPageStatus(health: HealthStatus): PageStatus {
    if (health.reachable && !health.maintenanceMode) return { status: 'ok' };
    if (health.reachable) {
        return {
            status: 'maintenance',
            message: health.message,
            isNetworkProblem: false,
        };
    }
    return {
        status: 'maintenance',
        message: null,
        isNetworkProblem: health.networkProblem,
    };
}

function hasRefreshToken(): boolean {
    return Boolean(localStorage.getItem('refreshToken'));
}

function SystemMaintenancePage() {
    const [pageStatus, setPageStatus] = useState<PageStatus>(() =>
        hasRefreshToken() ? { status: 'ok' } : { status: 'loading' },
    );

    const poll = useCallback(async () => {
        const health = await checkHealthWithRetries();
        setPageStatus(getPageStatus(health));
    }, []);

    useEffect(() => {
        poll();
        const interval = setInterval(poll, HEALTH_CHECK_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [poll]);

    if (pageStatus.status === 'loading') {
        return <Loader isPageLoader />;
    }
    if (pageStatus.status === 'ok') {
        return <Outlet />;
    }

    const { isNetworkProblem: hasNetworkProblem, message } = pageStatus;
    const title = hasNetworkProblem
        ? 'Connection Problem'
        : 'System Maintenance';
    const description = hasNetworkProblem
        ? "We can't reach our servers. Please check your internet connection and try again."
        : message ??
          'The system is currently undergoing maintenance. Please check back shortly.';

    return (
        <div className="flex w-full max-w-screen h-screen flex-col md:flex-col lg:flex-row">
            <div className="h-full lg:block xl:block lg:w-1/2 xl:w-9/12 sm:w-full overflow-hidden relative p-12 lg:p-24 flex items-center">
                <div className="flex flex-col h-full w-full justify-center lg:pb-[16rem]">
                    <img
                        src={logo}
                        alt="Cashbuild"
                        className="mb-2 w-32 lg:w-40"
                    />
                    <Title order={1} className="lg:text-[52px] text-primary">
                        {title}
                    </Title>
                    <Text size="lg" className="text-gray-400">
                        {description}
                    </Text>
                </div>
            </div>
            <div className="flex h-full w-full items-center justify-center">
                <img
                    src={SystemMaintenanceImage}
                    className="lg:h-full w-full lg:object-cover object-center"
                    alt="working-illustration"
                />
            </div>
        </div>
    );
}

export default SystemMaintenancePage;
